import { supabase } from "@/integrations/supabase/client";

export type AvatarState = {
  elementN: number;
  color: string;
  accessory: string;
};

export function parseAvatarState(raw?: string | null): AvatarState | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as unknown;
    if (!obj || typeof obj !== "object") return null;

    const color = (obj as any).color;
    if (typeof color !== "string" || !color.trim()) return null;

    const elementN = (obj as any).elementN;
    const accessory = (obj as any).accessory;

    return {
      elementN: typeof elementN === "number" && Number.isFinite(elementN) ? elementN : 1,
      color,
      accessory: typeof accessory === "string" && accessory.trim() ? accessory : "None",
    };
  } catch {
    return null;
  }
}

export function isLikelyUrl(value?: string | null): boolean {
  if (!value) return false;
  const v = value.trim();
  if (!v) return false;
  if (v.startsWith("{") || v.startsWith("[")) return false;
  if (v.startsWith("data:")) return true;
  if (v.startsWith("/") || v.startsWith("./")) return true;
  return /^https?:\/\//i.test(v);
}

export async function fetchUserAvatarConfig(userId: string): Promise<AvatarState> {
  const DEFAULT_AVATAR: AvatarState = {
    elementN: 6,
    color: '#60A5FA',
    accessory: 'None',
  };

  try {
    // 1. Try to fetch from user_avatar_config
    const { data: configData, error: configError } = await supabase
      .from('user_avatar_config')
      .select('body_color, shell_count, electron_count, accessory_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (!configError && configData) {
      // Use electron_count (atomic number) directly, or fall back to shell_count mapping
      let elementN = configData.electron_count;
      if (!elementN) {
        if (configData.shell_count === 1) elementN = 2; // Helium
        else if (configData.shell_count === 2) elementN = 6; // Carbon
        else if (configData.shell_count === 3) elementN = 12; // Magnesium
        else if (configData.shell_count === 4) elementN = 20; // Calcium
        else elementN = 6; // Carbon default
      }

      return {
        elementN,
        color: configData.body_color || '#60A5FA',
        accessory: configData.accessory_id || 'None',
      };
    }

    // 2. Fallback to profiles.avatar_url
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('avatar_url')
      .eq('user_id', userId)
      .maybeSingle();

    if (!profileError && profileData?.avatar_url) {
      const parsed = parseAvatarState(profileData.avatar_url);
      if (parsed) return parsed;
    }
  } catch (err) {
    console.error("Error in fetchUserAvatarConfig:", err);
  }

  return DEFAULT_AVATAR;
}


